import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { FlowsheetLabResultFilterHelper } from "@/Flowsheets/Helpers/FlowsheetLabResultFilterHelper";
import { LabResultsColumnHelper } from "@/Flowsheets/Helpers/LabResultsColumnHelper";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { LabResultsCareElement } from "@/Flowsheets/Models/CareElements/LabResultsCareElement";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import { CareComponentTable, CareComponentTableColumn } from "./CareComponentParts/CareComponentTable";
import { GridCell } from "./CareComponentParts/GridCell";
import { ICareComponentProps } from "./Types";

import styles from "./LabResultCareComponent.module.scss";

interface LabResultsCareComponent extends CareComponent {
    CareElements: LabResultsCareElement[];
}

function validateLabResultsCareElements(careElements: LabResultsCareElement[]): careElements is LabResultsCareElement[] {
    return careElements && careElements.length > 0 && careElements[0].CareElementType === CareElementType.LabResults;
}

const LabResultsCareComponent = React.memo(
    ({ isEditable, state, filterSettings, mutateState }: ICareComponentProps<LabResultsCareComponent>): JSX.Element => {
        const { getText } = useTranslation();

        const fixedColumns: CareComponentTableColumn[] = [
            {
                label: getText("AnalyseLabel"),
                isFixed: true,
                getKey: (data: LabResultsCareElement) => `analysis-${data.AnalysisStandardCode}`,
                render: (data: LabResultsCareElement) => (
                    <>
                        <p className={styles.analysis}>
                            {getText(`Flowsheets_LabResultsCareComponentLabel_${data.Name}`)}
                        </p>
                        <p className={styles.unit}>{data.Unit ? data.Unit : "-"} </p>
                    </>
                )
            },
            {
                label: getText("FrequencyLabel"),
                isFixed: true,
                getKey: (data: LabResultsCareElement) => `frequency-${data.AnalysisStandardCode}`,
                render: (data: LabResultsCareElement) => (
                    <GridCell
                        id={`labresult-frequency-${data.AnalysisStandardCode}`}
                        value={CareElementGridHelper.handleFrequencyLabel(data.Frequency, getText)}
                        showAlert={data.IsPastDue}
                        alertVariant="warning"
                        alertTooltipText={getText("Flowsheets_OutsideFrequencyTooltipLabel")}
                    />
                )
            },
            {
                label: getText("TargetLabel"),
                isFixed: true,
                getKey: (data: LabResultsCareElement) => `target-${data.AnalysisStandardCode}`,
                render: (data: LabResultsCareElement) => (
                    <p> {CareElementGridHelper.handleTargetLabel(data.Operator, data.TargetRange)} </p>
                )
            }
        ];

        let filteredCareElements: LabResultsCareElement[] = [];
        let dateColumns: CareComponentTableColumn[] = [];

        if (validateLabResultsCareElements(state.CareElements)) {
            filteredCareElements = FlowsheetLabResultFilterHelper.filterCareElements(state.CareElements, filterSettings);
            FlowsheetLabResultFilterHelper.filterLabResultObservations(filteredCareElements, filterSettings);

            const { dates, dateLabObservations } = LabResultsColumnHelper.processLabResultsColumns(filteredCareElements);

            dateColumns = dates.map((date, columnIndex) => ({
                label: date.toLocaleDateString("en-CA"),
                isFixed: false,
                getKey: (data: LabResultsCareElement, index: number) => `result-${data.AnalysisStandardCode}-${index}-${date.toLocaleDateString("en-CA")}-${columnIndex}`,
                render: (data: LabResultsCareElement) => {
                    const observationData = LabResultsColumnHelper.getObservationForColumn(
                        dateLabObservations,
                        dates,
                        date,
                        data.AnalysisStandardCode,
                        columnIndex
                    );

                    return (
                        <GridCell
                            id={`labresult-result-${data.AnalysisStandardCode}-${date.toLocaleDateString("en-CA")}-${columnIndex}`}
                            value={observationData?.observation.Value?.toString() ?? ""}
                            showAlert={observationData?.observation.IsOutsideOfTargetValue ?? false}
                            alertVariant="alarming"
                        />
                    );
                }
            }));
        }

        return (
            <div className={styles.labResultCareComponent}>
                <CareComponentTable
                    id="labResultTable"
                    columns={fixedColumns.concat(dateColumns)}
                    data={filteredCareElements}
                />

                <CareComponentNotes
                    id={`labResultNote-${state.Id}`}
                    dataCy="LabResultNotes"
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                />
            </div>
        );
    }
);

export { LabResultsCareComponent };
